<template>
  <section id="skills" class="dark-section">
    <div class="container-fluid">
      <h1 class="section-header">{{ heading }}</h1>
      <br>
      <p class="section-subheading">My technical expertise and professional competencies</p>
      
      <!-- Search and Filter -->
      <div class="skills-controls">
        <div class="search-container">
          <input 
            type="text" 
            v-model="searchQuery" 
            placeholder="Search skills..." 
            class="search-input"
          />
          <i class="fas fa-search search-icon"></i>
        </div>
        
        <div class="skill-level-filter">
          <span class="filter-label">Expertise Level:</span>
          <div class="level-buttons">
            <button 
              v-for="level in skillLevels" 
              :key="level.value"
              @click="toggleLevelFilter(level.value)"
              :class="[
                'level-btn', 
                { active: selectedLevels.includes(level.value) }
              ]"
            >
              {{ level.label }}
            </button>
          </div>
        </div>
      </div>
      
      <!-- Skills Grid with Mastery Visualization -->
      <div class="mastery-grid">
        <div 
          v-for="skill in filteredSkills" 
          :key="skill.name"
          class="mastery-item"
          :class="getMasteryClass(skill.level)"
        >
          <div class="skill-header">
            <i :class="[skill.icon, 'skill-icon']"></i>
            <div class="skill-title-container">
              <h3>{{ skill.name }}</h3>
              <div class="skill-category">{{ skill.category }}</div>
            </div>
          </div>
          
          <div class="mastery-visualization">
            <div class="mastery-dots">
              <span 
                v-for="n in 5" 
                :key="n"
                class="mastery-dot"
                :class="{ filled: n <= Math.ceil(skill.level/20) }"
              ></span>
            </div>
            <div class="mastery-label">{{ getMasteryLabel(skill.level) }}</div>
          </div>
          
          <p class="skill-description">{{ skill.description }}</p>
          
          <div v-if="skill.projects && skill.projects.length" class="related-projects">
            <span class="related-label">Relevant Projects:</span>
            <div class="project-links">
              <span 
                v-for="(project, index) in skill.projects" 
                :key="index"
                class="project-link"
              >
                {{ project }}
              </span>
            </div>
          </div>
        </div>
      </div>
      
      <!-- No Results Message -->
      <div v-if="filteredSkills.length === 0" class="no-results">
        <i class="fas fa-search-minus"></i>
        <p>No skills match your search criteria. Try adjusting your filters.</p>
      </div>
    </div>
    <Arrow />
  </section>
</template>

<script>
import data from "../../data/data.json";
import Arrow from "../components/Arrow.vue";

export default {
  name: "SkillsGrid",
  components: {
    Arrow,
  },
  data() {
    return {
      heading: data.main.headings.skills || "Skills & Expertise",
      skills: data.skills.items || [],
      searchQuery: "",
      selectedLevels: ["beginner", "intermediate", "advanced", "expert"],
      skillLevels: [
        { label: "Beginner", value: "beginner" },
        { label: "Intermediate", value: "intermediate" },
        { label: "Advanced", value: "advanced" },
        { label: "Expert", value: "expert" }
      ]
    };
  },
  computed: {
    filteredSkills() {
      return this.skills.filter(skill => {
        // Filter by search query
        const matchesSearch = skill.name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
                             skill.description.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
                             skill.category.toLowerCase().includes(this.searchQuery.toLowerCase());
        
        // Filter by selected level
        const skillLevel = this.getMasteryValue(skill.level);
        const matchesLevel = this.selectedLevels.includes(skillLevel);
        
        return matchesSearch && matchesLevel;
      });
    }
  },
  methods: {
    toggleLevelFilter(level) {
      if (this.selectedLevels.includes(level)) {
        // Don't allow removing all filters
        if (this.selectedLevels.length > 1) {
          this.selectedLevels = this.selectedLevels.filter(l => l !== level);
        }
      } else {
        this.selectedLevels.push(level);
      }
    },
    getMasteryValue(level) {
      if (level < 50) return "beginner";
      if (level < 70) return "intermediate";
      if (level < 90) return "advanced";
      return "expert";
    },
    getMasteryLabel(level) {
      if (level < 50) return "Beginner";
      if (level < 70) return "Intermediate";
      if (level < 90) return "Advanced";
      return "Expert";
    },
    getMasteryClass(level) {
      return `mastery-${this.getMasteryValue(level)}`;
    }
  }
};
</script>

<style lang="scss" scoped>
// Define variables inline for dark theme
$primary-color: #a334db;
$dark-bg-color: #222222;
$dark-card-bg: rgba(25, 25, 25, 0.8);
$light-text: #ffffff;
$gray-text: #e0e0e0;
$accent-text: #a334db;
$border-color: #333333;
$spacer-lg: 3rem;

#skills {
  background-color: $dark-bg-color;
  color: $light-text;
  
  .container-fluid {
    max-width: 5000px;
    margin: $spacer-lg auto;
    padding-left: 2rem;
    padding-right: 2rem;
  }
  
  .section-header {
    color: $light-text;
    margin-top: 2rem;
  }
  
  .section-subheading {
    text-align: center;
    color: $gray-text;
    margin-bottom: 2rem;
  }
  
  .skills-controls {
    display: flex;
    flex-wrap: wrap;
    gap: 1.5rem;
    justify-content: center;
    align-items: center;
    margin-bottom: 2rem;
    
    .search-container {
      position: relative;
      
      .search-input {
        background-color: rgba(40, 40, 40, 0.8);
        color: $light-text;
        border: 1px solid $border-color;
        padding: 0.75rem 1rem 0.75rem 2.5rem;
        border-radius: 30px;
        font-size: 1rem;
        width: 250px;
        transition: all 0.3s ease;
        
        &:focus {
          border-color: $primary-color;
          box-shadow: 0 0 0 2px rgba($primary-color, 0.3);
          outline: none;
          width: 300px;
        }
        
        &::placeholder {
          color: #999999;
        }
      }
      
      .search-icon {
        position: absolute;
        left: 1rem;
        top: 50%;
        transform: translateY(-50%);
        color: #999999;
        pointer-events: none;
      }
    }
    
    .skill-level-filter {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 0.75rem;
      
      .filter-label {
        color: $gray-text;
        font-size: 0.9rem;
      }
      
      .level-buttons {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        
        .level-btn {
          background-color: rgba(40, 40, 40, 0.8);
          color: $gray-text;
          border: 1px solid $border-color;
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.3s ease;
          
          &:hover {
            background-color: rgba(60, 60, 60, 0.8);
          }
          
          &.active {
            background-color: $primary-color;
            border-color: $primary-color;
            color: $light-text;
          }
          
          &.active[class*="beginner"] {
            background-color: #8096a5;
            border-color: #8096a5;
            color: $light-text;
          }
          
          &.active[class*="intermediate"] {
            background-color: #4caf50;
            border-color: #4caf50;
            color: $light-text;
          }
          
          &.active[class*="advanced"] {
            background-color: #ff9800;
            border-color: #ff9800;
            color: $light-text;
          }
          
          &.active[class*="expert"] {
            background-color: #f44336;
            border-color: #f44336;
            color: $light-text;
          }
        }
      }
    }
  }
  
  .mastery-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 1.5rem;
    margin-top: 2rem;
    justify-content: center;
    max-width: 1500px;
    margin-left: auto;
    margin-right: auto;
  }
  
  .mastery-item {
    background-color: $dark-card-bg;
    border-radius: 8px;
    padding: 1.5rem;
    box-shadow: 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    position: relative;
    overflow: hidden;
    color: $gray-text;
    width: 280px;
    flex-grow: 0;
    flex-shrink: 0;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 5px;
      height: 100%;
      transition: all 0.3s ease;
    }
    
    &:hover {
      transform: translateY(-5px);
    }
    
    &.mastery-beginner::before {
      background-color: #8096a5;
    }
    
    &.mastery-intermediate::before {
      background-color: #4caf50;
    }
    
    &.mastery-advanced::before {
      background-color: #ff9800;
    }
    
    &.mastery-expert::before {
      background-color: #f44336;
    }
    
    .skill-header {
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 1rem;
      
      .skill-icon {
        font-size: 1.8rem;
        margin-right: 1rem;
        color: $accent-text;
        width: 40px;
        text-align: center;
      }
      
      .skill-title-container {
        flex: 1;
        
        h3 {
          font-size: 1.2rem;
          margin: 0 0 0.2rem 0;
          color: $light-text;
        }
        
        .skill-category {
          font-size: 0.8rem;
          color: #a0a0a0;
          text-transform: uppercase;
          letter-spacing: 1px;
        }
      }
    }
    
    .mastery-visualization {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 1rem;
      
      .mastery-dots {
        display: flex;
        gap: 0.25rem;
        
        .mastery-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background-color: #444444;
          transition: all 0.3s ease;
          
          &.filled {
            background-color: $primary-color;
          }
        }
      }
      
      .mastery-label {
        font-size: 0.8rem;
        font-weight: 600;
        color: $light-text;
      }
    }
    
    .skill-description {
      color: $gray-text;
      font-size: 0.9rem;
      margin-bottom: 1rem;
      line-height: 1.5;
    }
    
    .related-projects {
      margin-top: auto;
      
      .related-label {
        display: block;
        font-size: 0.8rem;
        color: #a0a0a0;
        margin-bottom: 0.5rem;
      }
      
      .project-links {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        justify-content: center;
        
        .project-link {
          background-color: rgba(40, 40, 40, 0.8);
          color: $gray-text;
          padding: 0.2rem 0.6rem;
          border-radius: 15px;
          font-size: 0.8rem;
          transition: all 0.2s ease;
          
          &:hover {
            background-color: rgba(60, 60, 60, 0.8);
          }
        }
      }
    }
  }
  
  .no-results {
    text-align: center;
    padding: 3rem 0;
    color: $gray-text;
    
    i {
      font-size: 3rem;
      margin-bottom: 1rem;
    }
    
    p {
      font-size: 1.2rem;
    }
  }
}

// Responsive adjustments
@media (max-width: 768px) {
  #skills {
    .container-fluid {
      padding-left: 1rem;
      padding-right: 1rem;
    }
    
    .skills-controls {
      flex-direction: column;
      align-items: stretch;
      
      .search-container {
        width: 100%;
        
        .search-input {
          width: 100%;
          
          &:focus {
            width: 100%;
          }
        }
      }
      
      .skill-level-filter {
        flex-direction: column;
        align-items: flex-start;
        
        .level-buttons {
          width: 100%;
          justify-content: flex-start;
        }
      }
    }
    
    .mastery-grid {
      .mastery-item {
        width: 100%;
        max-width: 400px;
        margin: 0 auto;
      }
    }
  }
}

// Additional breakpoint for medium-sized screens
@media (max-width: 992px) and (min-width: 769px) {
  #skills {
    .skill-level-filter {
      .level-buttons {
        flex-wrap: wrap;
        justify-content: flex-start;
      }
    }
  }
}

// Special handling for very small screens
@media (max-width: 480px) {
  #skills {
    .skill-level-filter {
      .level-buttons {
        justify-content: center;
        
        .level-btn {
          flex: 1 0 calc(50% - 0.5rem);
          text-align: center;
          padding: 0.5rem 0.5rem;
          font-size: 0.8rem;
        }
      }
    }
  }
}
</style>